"use client";

import {
  ButtonSize,
  ButtonType,
  ButtonVariation,
  ButtonVisual,
} from "@/types/button";

import * as Checkbox from "@radix-ui/react-checkbox";
import { CheckIcon } from "@radix-ui/react-icons";
import { useState } from "react";
import Button from "../Button";
import Logo from "../Logo";
import AsyncScripts from "../scripts/AsyncScripts";
import { setCookie } from "@/utils/clientCookies";
import { MonitoringEvent } from "@/utils/events";

type Props = {};

const CustomModalConsent = (props: Props) => {
  const [activeScript, setActiveScript] = useState(false);
  const [customSettings, setCustomSettings] = useState(false);
  const [tracking, setTracking] = useState(true);
  const [marketing, setMarketing] = useState(true);
  const [all, setAll] = useState(true);

  const setAllCookies = () => {
    try {
      setCookie("activeConsent", "true");
      setCookie("enableEssential", "true");
      setCookie("enableFunctional", "true");
      setCookie("enableMarketing", "true");
      setCookie("enableTracking", "true");
      closeModal();
    } catch (error) {
      closeModal();  
      MonitoringEvent("setAllCookies", "error");
    }
  };
  const setCustomCookies = () => {
    try {
      setCookie("activeConsent", "true");
      setCookie("enableEssential", "true");
      setCookie("enableFunctional", "true");
      setCookie("enableMarketing", marketing ? "true" : "false");
      setCookie("enableTracking", tracking ? "true" : "false"); 
      closeModal(); 
    } catch (error) {
      closeModal(); 
      MonitoringEvent("setCustomCookies", "error");
    }
  };

  const closeModal = () => {
    window.document.body.classList.remove("overflow-hidden");
    window.document.getElementById("consent_overlay")?.remove();
    window.document.getElementById("consent_content")?.remove();
    setActiveScript(true);
  };

  const swapAll = () => {
    setMarketing(!all);
    setTracking(!all);
    setAll(!all);
  };
  const swapMarketing = () => {
    tracking && !marketing ? setAll(true) : setAll(false);
    setMarketing(!marketing);
  };
  const swapTracking = () => {
    !tracking && marketing ? setAll(true) : setAll(false);
    setTracking(!tracking);
  };

  return (
    <>
      {activeScript && <AsyncScripts />}
      <div
        data-state="open"
        className="fixed inset-0 z-10 cursor-pointer bg-secondary-700/60 md:backdrop-blur-sm"
        id="consent_overlay"
        // style={{"pointer-events: auto;"}}
        data-aria-hidden="true"
        aria-hidden="true"
      ></div>
      <div
        className="right left fixed bottom-1 z-50 w-full focus:outline-none"
        id="consent_content"
      >
        {!customSettings ? (
          <div className="m-4 grid grid-cols-1 gap-4 rounded-md bg-white p-4 text-[12.5px] leading-normal md:grid-cols-5 md:flex-row lg:grid-cols-7">
            <div className="col-span-1 flex flex-col gap-2">
              <Logo type="logo" className="hidden h-auto w-32 md:flex" />
              <p className="text-[16px] font-semibold leading-[22px] text-secondary">
                Bevor Sie zu {process.env.NEXT_PUBLIC_SITE === "benu" ? "benu.at" : "benu-bestattung.de"} weitergehen Auf dieser Website nutzen wir
                Cookies.
              </p>
            </div>
            <div className="col-span-1 flex flex-col gap-2  md:col-span-3 lg:col-span-5">
              <p>
                Auf dieser Website nutzen wir Cookies. Diese helfen uns bei der
                Bereitstellung unserer Dienste. Die Verarbeitung dient der:
                Einbindung von Inhalten externen Diensten & Elementen;
                statistischen Analyse/Messung; personalisierter Werbung sowie,
                Einbindung sozialer Medien.
              </p>
              <p>
                Je nach Funktion werden dabei Daten an Partner weitergegeben und
                von diesen verarbeitet. Diese Einwilligung ist freiwillig, für
                die Nutzung unserer Website nicht erforderlich und kann
                jederzeit widerrufen werden.
              </p>
              <p>
                Indem Sie auf "Akzeptieren" klicken, willigen Sie zugleich ein,
                dass Ihre Daten in den USA von Google verarbeitet werden. Die
                Übermittlung der Daten findet nicht statt, wenn Sie in den
                Einstellungen Zielorientiere- und Marketing Cookies nicht
                auswählen. Nähere Informationen dazu und zu Ihren Rechten als
                Benutzer finden Sie in unserer Datenschutzerklärung.
              </p>
            </div>
            <div className="flex w-full flex-col justify-center gap-1 md:h-full">
              <Button
                visual={ButtonVisual.PRIMARY}
                variation={ButtonVariation.SOLID}
                label="AKZEPTIEREN"
                link="link"
                buttonId="all-cookies"
                // size={ButtonSize.LARGE}
                type={ButtonType.ACTION}
                onClick={() => {
                  setAllCookies();
                }}
              />
              <Button
                visual={ButtonVisual.SECONDARY}
                variation={ButtonVariation.TEXT}
                label="EINSTELLUNGEN"
                link="link"
                buttonId="go-custom-cookies"
                type={ButtonType.ACTION}
                size={ButtonSize.SMALL}
                onClick={() => {
                  setCustomSettings(!customSettings);
                }}
              />
            </div>
          </div>
        ) : (
          <div className="m-4 grid grid-cols-1 gap-4 rounded-md bg-white p-4 text-[13px] leading-normal md:grid-cols-5 md:flex-row lg:grid-cols-7">
            <div className="col-span-1 flex flex-col gap-2">
              <Logo type="logo" className="hidden h-auto w-32 md:flex" />
              <p className="text-[16px] font-semibold leading-[22px] text-secondary">
                Cookie-Einstellungen
              </p>
            </div>
            <div className="col-span-1 flex flex-col gap-2  md:col-span-3 lg:col-span-5">
              <p>
                Hier können Sie die Cookie-Einstellungen verschiedener Tools,
                die auf dieser Domain und ihren Subdomains verwendet werden,
                einsehen bzw. ändern.
              </p>

              <span className="flex flex-col gap-3 md:flex-row ">
                <span className="checkbox">
                  <Checkbox.Root
                    className="checkbox"
                    id="all"
                    checked={all}
                    onCheckedChange={swapAll}
                  >
                    <Checkbox.Indicator>
                      <CheckIcon />
                    </Checkbox.Indicator>
                  </Checkbox.Root>
                  <label htmlFor="all">Alle auswählen</label>
                </span>

                <span className="checkbox">
                  <Checkbox.Root
                    className="checkbox"
                    id="essential"
                    checked
                    disabled
                  >
                    <Checkbox.Indicator>
                      <CheckIcon />
                    </Checkbox.Indicator>
                  </Checkbox.Root>
                  <label htmlFor="essential">Notwendige Cookies</label>
                </span>

                <span className="checkbox">
                  <Checkbox.Root
                    className="checkbox"
                    id="functional"
                    checked
                    disabled
                  >
                    <Checkbox.Indicator>
                      <CheckIcon />
                    </Checkbox.Indicator>
                  </Checkbox.Root>
                  <label htmlFor="functional">Funktionelle Cookies</label>
                </span>

                <span className="checkbox">
                  <Checkbox.Root
                    className="checkbox"
                    id="tracking"
                    checked={tracking}
                    onCheckedChange={swapTracking}
                  >
                    <Checkbox.Indicator>
                      <CheckIcon />
                    </Checkbox.Indicator>
                  </Checkbox.Root>
                  <label htmlFor="tracking">Zielorientierte Cookies</label>
                </span>

                <span className="checkbox">
                  <Checkbox.Root
                    className="checkbox"
                    id="marketing"
                    checked={marketing}
                    onCheckedChange={swapMarketing}
                  >
                    <Checkbox.Indicator>
                      <CheckIcon />
                    </Checkbox.Indicator>
                  </Checkbox.Root>
                  <label htmlFor="marketing">Marketing Cookies</label>
                </span>
              </span>
            </div>
            <div className="flex w-full flex-col justify-center gap-2 md:h-full">
              <Button
                visual={ButtonVisual.PRIMARY}
                variation={ButtonVariation.SOLID}
                label="Alle Akzeptieren"
                link="link"
                buttonId="custom-all-cookies"
                size={ButtonSize.MEDIUM}
                type={ButtonType.ACTION}
                onClick={() => {
                  setAllCookies();
                }}
              />
              <Button
                visual={ButtonVisual.SECONDARY}
                variation={ButtonVariation.OUTLINE}
                label="Einstellung Speichern"
                link="link"
                buttonId="custom-cookies"
                size={ButtonSize.MEDIUM}
                type={ButtonType.ACTION}
                onClick={() => {
                  setCustomCookies();
                }}
              />
              <Button
                visual={ButtonVisual.SECONDARY}
                variation={ButtonVariation.TEXT}
                label="Zurück"
                link="link"
                buttonId="back-all-cookies"
                type={ButtonType.ACTION}
                size={ButtonSize.SMALL}
                onClick={() => {
                  setCustomSettings(!customSettings);
                }}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default CustomModalConsent;
